import { NO_POS } from '#core/lib/expr/ast.ts';
import { colNodeLabels } from '#core/lib/expr/column-ref.ts';
import { type AstInput, astShape, embeddedString, extractLambdaBody } from '#core/lib/expr/normalize.ts';
import type { ColumnRefInArg } from '#core/lib/expr/op-meta.ts';
import { formatDisplayInstant } from '#core/lib/expr/ops/transform/date-utils.ts';
import { cdfViewNativeOf } from '#core/lib/expr/providers/cdf-view.ts';
import { exprColRefs } from '#core/lib/expr/schema/check.ts';
import { type ColumnType, unwrapNullable } from '#core/lib/expr/schema/types.ts';
import { type Dataset, expectDataset, isLazy, type MaterializedDataset, type Value } from '#core/lib/expr/value.ts';
import { closestMatches } from '#core/lib/utils/closest-matches.ts';

import { computeAutoStats } from '#plugin-chat/lib/tool/auto-stats.ts';
import {
  computeColumnFrequencies,
  type DatasetSummary,
  isPrimitiveLeafKind,
  MAX_ENUM_DISTINCT,
  summarizeDataset,
} from '#plugin-chat/lib/tool/dataset-summary.ts';
import { expressionToAst } from '#plugin-chat/lib/tool/tool-identity.ts';

/**
 * Per-column recovery hint attached to a {@link DatasetSummary} when a
 * query returned no rows OR a column came back fully null. The agent
 * uses this to correct hallucinated filter values: the values shown
 * are the column's actual domain at the source, not a guess.
 */
export type RecoveryEntry =
  | Readonly<{
      column: string;
      kind: 'distinct';
      /** Top-N distinct values by frequency, descending. Capped at {@link MAX_ENUM_DISTINCT}. */
      values: readonly string[];
      /** Total distinct value count in the source — may exceed `values.length`. */
      distinctCount: number;
      /**
       * "Did you mean?" suggestions for filter literals that came close
       * to (but didn't match) a source value. Populated when the failing
       * query had a `(= <column> "literal")` or `(in <column> [...])`
       * predicate and `literal` is close to some actual value (per the
       * fuse.js Bitap-based ranker). Omitted when no literal-typo is
       * detected.
       */
      closeMatches?: readonly Readonly<{ typed: string; suggestion: string }>[];
    }>
  | Readonly<{
      column: string;
      kind: 'range';
      /** ISO string for dates/timestamps, decimal string for numerics; null if no rows. */
      min: string | null;
      max: string | null;
    }>;

/**
 * Column-literal pair lifted out of a filter predicate — e.g. `(= status
 * "actve")` yields `{ column: 'status', literal: 'actve' }`. Drives the
 * fuzzy-match enrichment on recovery distinct entries so the agent
 * sees "did you mean: active?" when its filter literal is a typo for an
 * actual source value.
 */
export type FilterLiteralPair = Readonly<{ column: string; literal: string }>;

/**
 * Tool-supplied fetcher that returns the recovery *source dataset* — the
 * upstream the agent's failing filter was applied over. For
 * `transformDataset` this is the expression's seed binding, re-resolved by
 * name and served from the session fetch cache. Returns `null` when no
 * recovery source can be determined. Fetcher failures throw —
 * `extendSummaryWithRecovery` catches and logs them.
 */
export type RecoverySourceFetcher = () => Promise<MaterializedDataset | null>;

/**
 * The CDF view + absorbed constraints behind a knowledge-graph-rooted
 * lazy dataset. SSOT for "is this a CDF-view-backed dataset, and if so
 * under which view + what was pushed down?" — a CDF-view source carries
 * `kind:'provider'` with the CDF view provider's `native` blob, read
 * through the sanctioned `cdfViewNativeOf` accessor, which exposes the
 * recovery-relevant facts: the view name, the pushed-down bare `filter`
 * predicate, and the node `limit`.
 *
 * Returns `null` for materialised datasets, timeseries-rooted lazy
 * datasets, and any provider source that is not the CDF view provider.
 * Since `transform` only appends to `plan.ops`, this works uniformly
 * across single-query and chained transform datasets — they share the
 * same root source.
 */
export const cdfSourceOf = (
  ds: Dataset,
): { readonly view: string; readonly filter?: AstInput; readonly limit?: number } | null => {
  if (!isLazy(ds)) {
    return null;
  }
  const native = cdfViewNativeOf(ds.plan.source);
  return native === null ? null : { view: native.view, filter: native.filter, limit: native.limit };
};

/**
 * The view externalId of a CDF-view-rooted lazy dataset, or `null`. A thin
 * projection of {@link cdfSourceOf} kept as the name the tool `execute`
 * boundaries gate on ("is this dataset recoverable as a view query?").
 */
export const qkgViewOf = (ds: Dataset): string | null => cdfSourceOf(ds)?.view ?? null;

/**
 * Human-readable source label for the result envelope's `source` field.
 * Resolves to the most specific origin available:
 *
 *   - the CDF view externalId when the dataset is view-rooted (the
 *     `query` op's CDF view provider source);
 *   - otherwise the originating provider's `id` for any other lazy
 *     dataset (e.g. `'timeseries'`) — every source descriptor is a
 *     provider source, so the provider id is the meaningful label;
 *   - otherwise the literal `'materialized'`.
 */
export const sourceLabelOf = (ds: Dataset): string => {
  const view = qkgViewOf(ds);
  if (view !== null) {
    return view;
  }
  if (!isLazy(ds)) {
    return 'materialized';
  }
  return ds.plan.source.providerId;
};

/**
 * Resolve the limit value to pass to `summarizeDataset` for the
 * `truncated` signal — or `undefined` to suppress it. The signal fires
 * when `rowCount === limit`, so we only want to pass a number when the
 * SOURCE cap is the actual binding constraint:
 *
 *   - For non-view-rooted datasets there's no inherent source cap →
 *     return `undefined` (no truncated signal).
 *   - For a view-rooted lazy dataset with no source `limit` → no source
 *     cap → return `undefined`.
 *   - For a view-rooted lazy dataset whose plan contains a top-level
 *     `(limit M)` op with M ≤ source limit → the op is the cap; the
 *     source cap isn't doing the work. "Raise the source limit" would
 *     be misleading advice (raising it wouldn't change rowCount), so
 *     suppress the signal by returning `undefined`.
 *   - Otherwise → return the source limit; if rowCount equals it the
 *     source genuinely truncated and the agent should know.
 *
 * Top-level `(limit M)` is appended to `plan.ops` by the dispatcher's
 * lazy branch with `op === 'limit'` and `args.params === [M]` (the
 * dataset positional is stripped before capture).
 */
const effectiveTruncationLimit = (ds: Dataset): number | undefined => {
  const cdfSource = cdfSourceOf(ds);
  if (cdfSource === null || !isLazy(ds)) {
    return undefined;
  }
  const sourceLimit = cdfSource.limit;
  if (sourceLimit === undefined) {
    return undefined;
  }
  // Min of any plan-node-level `(limit M)` ops — if any caps at or below
  // the source cap, the source is no longer the binding constraint.
  const opLimits = ds.plan.ops.flatMap((node) => {
    if (node.op !== 'limit') {
      return [];
    }
    const m = node.args.params?.[0];
    return typeof m === 'number' ? [m] : [];
  });
  const opCap = opLimits.length > 0 ? Math.min(...opLimits) : undefined;
  if (opCap !== undefined && opCap <= sourceLimit) {
    return undefined;
  }
  return sourceLimit;
};

/**
 * Materialise a Dataset and build its LLM-facing {@link DatasetSummary}.
 * The shared base for the view-query and transform tools; the transform
 * tool layers local recovery on top via {@link summarizeWithLocalRecovery}.
 */
export const materializeAndSummarize = async (params: {
  ds: Dataset;
  materialize: (ds: Dataset) => Promise<MaterializedDataset>;
}): Promise<{ mat: MaterializedDataset; summary: DatasetSummary }> => {
  const { ds, materialize } = params;
  const mat = await materialize(ds);
  return { mat, summary: summarizeDataset(mat, { limit: effectiveTruncationLimit(ds) }) };
};

/**
 * The bare binding symbol a transform expression bottoms out at, or `null`
 * when the seed is anything else (an inline `(query …)` source op, an
 * embedded literal, unparseable input). Thread-first desugar nests the
 * upstream at `params[0]`, so following first positional params reaches
 * the seed of arbitrarily chained pipelines.
 */
const seedBindingOf = (expression: string): string | null => {
  const seedOf = (node: unknown): string | null => {
    const shape = astShape(node);
    if (shape.kind === 'symbolRef') {
      return shape.sym;
    }
    return shape.kind === 'opCall' ? seedOf(shape.call.params?.[0] ?? null) : null;
  };
  try {
    return seedOf(expressionToAst(expression));
  } catch {
    return null;
  }
};

/**
 * {@link materializeAndSummarize}, plus a recovery context mined from the
 * expression's seed binding when the result is empty or a column is fully
 * null. The recovery source is the upstream dataset the transform ran
 * over — re-resolved by binding name and served from the session fetch
 * cache its producing turn already populated, so recovery never mints a
 * new CDF query shape. Expressions that don't bottom out at a bare
 * binding (an inline `(query …)` seed would require a fresh CDF fetch)
 * get the base summary unchanged.
 */
export const summarizeWithLocalRecovery = async (params: {
  ds: Dataset;
  expression: string;
  evaluate: (expression: string) => Promise<Value>;
  materialize: (ds: Dataset) => Promise<MaterializedDataset>;
}): Promise<{ mat: MaterializedDataset; summary: DatasetSummary }> => {
  const { ds, expression, evaluate, materialize } = params;
  const { mat, summary: base } = await materializeAndSummarize({ ds, materialize });
  const seed = seedBindingOf(expression);
  if (seed === null) {
    return { mat, summary: base };
  }
  const summary = await extendSummaryWithRecovery({
    summary: base,
    expression,
    // Lazy — `extendSummaryWithRecovery` fetches only after the
    // empty/all-null trigger has fired.
    fetchSource: async () => {
      const value = await evaluate(seed);
      return materialize(expectDataset(value, 'recovery source', NO_POS));
    },
  });
  return { mat, summary };
};

/**
 * SSOT for "walk every filter-predicate AST in a script". Handles every
 * filter-carrying form recovery may encounter:
 *
 *   - `(filter ds <lambda>)` — the standalone transform op; the lambda is
 *     the second positional (`params[1]`, after the dataset).
 *   - `(query "View" (filter <lambda>) …)` / `(datapoints (filter
 *     <lambda>) …)` — a source op's trailing constraint form, where the
 *     `(filter …)` carries the lambda as its FIRST positional (`params[0]`,
 *     no dataset). These trailing forms are read structurally, not
 *     dispatched, so the walker treats them as data.
 *
 * Both carry IDENTICAL predicate syntax (`#(predicate-body)` desugared to
 * `(fn [%] body)`), so a single {@link extractLambdaBody} decodes them.
 * Used by `extractFilterColumns` (collect column refs) and
 * `extractFilterLiterals` (collect column+literal pairs); each gets the
 * bare predicate expression so it doesn't re-derive the lambda traversal.
 * Recurses into nested op calls and vectors so multi-stage chains with
 * several filter ops are fully covered.
 */
const walkFilterPredicates = <T>(ast: AstInput, extract: (pred: unknown) => readonly T[]): readonly T[] => {
  const collect = (node: unknown): readonly T[] => {
    const shape = astShape(node);
    if (shape.kind === 'vector') {
      return shape.elements.flatMap(collect);
    }
    if (shape.kind !== 'opCall') {
      return [];
    }
    const call = shape.call;
    // A `(filter …)` form carries its lambda at `params[1]` when invoked as
    // the standalone op `(filter ds <lambda>)`, but at `params[0]` when it
    // is a source op's trailing constraint `(filter <lambda>)`. Read
    // whichever positional yields a lambda body so both shapes resolve; a
    // dataset positional (`params[0]` of the standalone op) is not a lambda
    // and extracts nothing, so no double-count.
    const fromFilters = (call.name === 'filter' ? [call.params?.[0], call.params?.[1]] : []).flatMap((lambda) => {
      if (lambda === undefined) {
        return [];
      }
      const body = extractLambdaBody(lambda)?.[0];
      return body === undefined ? [] : extract(body);
    });
    const fromParams = (call.params ?? []).flatMap(collect);
    const fromKwargs = Object.entries(call)
      .filter(([key]) => key !== 'name' && key !== 'params')
      .flatMap(([, value]) => collect(value));
    return [...fromFilters, ...fromParams, ...fromKwargs];
  };
  return collect(ast);
};

/**
 * Predicate-level SSOT for filter-column extraction. Walks a bare
 * predicate AST (the inside of a `(filter …)` form, or a CDF view
 * source's pushed-down filter AST) and returns every column ref present
 * that resolves to a known column. Reused by {@link extractFilterColumns}
 * for whole-AST scans and by `summarizeWithRecovery` for upstream filter
 * AST nodes that live outside the chained expression.
 */
export const extractFilterColumnsFromPredicate = (
  predicate: unknown,
  knownColumns: ReadonlySet<string>,
): readonly string[] => {
  const refToColumn = (ref: ColumnRefInArg): string | null => {
    if (ref.colLabels === undefined) {
      return knownColumns.has(ref.name) ? ref.name : null;
    }
    return ref.colLabels.find((label) => knownColumns.has(label)) ?? null;
  };
  return exprColRefs(predicate, '').flatMap((ref) => {
    const col = refToColumn(ref);
    return col === null ? [] : [col];
  });
};

export const extractFilterColumns = (ast: AstInput, knownColumns: ReadonlySet<string>): readonly string[] =>
  Array.from(new Set(walkFilterPredicates(ast, (pred) => extractFilterColumnsFromPredicate(pred, knownColumns))));
/**
 * Resolve an AST node to a known column name if the node is a bare
 * symbolRef (`foo`) or a single-/multi-label `(col …)` opcall whose
 * first matching label is in `knownColumns`. Shared between literal
 * extraction here and column extraction in {@link extractFilterColumns}.
 */
const asKnownColumn = (node: unknown, knownColumns: ReadonlySet<string>): string | null => {
  const shape = astShape(node);
  if (shape.kind === 'symbolRef') {
    return knownColumns.has(shape.sym) ? shape.sym : null;
  }
  if (shape.kind === 'opCall' && shape.call.name === 'col') {
    const labels = colNodeLabels(node) ?? [];
    return labels.find((l) => knownColumns.has(l)) ?? null;
  }
  return null;
};

/** Read a string-literal AST node (a self-evaluating embedded value), else null. */
const asStringLiteral = (node: unknown): string | null => embeddedString(node) ?? null;

/**
 * Lift the (column, literal) pair from a binary `(= a b)` predicate
 * when one side is a known column ref and the other a string literal.
 * Returns `[]` for column-vs-column, literal-vs-literal, or non-string
 * literals — fuzzy match only applies to string equality.
 */
const equalityPair = (params: readonly unknown[], knownColumns: ReadonlySet<string>): readonly FilterLiteralPair[] => {
  const [a, b] = params;
  const colA = asKnownColumn(a, knownColumns);
  const litB = asStringLiteral(b);
  if (colA && litB !== null) {
    return [{ column: colA, literal: litB }];
  }
  const colB = asKnownColumn(b, knownColumns);
  const litA = asStringLiteral(a);
  if (colB && litA !== null) {
    return [{ column: colB, literal: litA }];
  }
  return [];
};

/**
 * Lift every (column, literal) pair from an `(in <colRef> ["lit" …])`
 * vector-membership predicate. Returns `[]` when the column ref isn't
 * known, the second arg isn't a vector, or the vector contains no
 * string literals.
 */
const inMembershipPairs = (
  params: readonly unknown[],
  knownColumns: ReadonlySet<string>,
): readonly FilterLiteralPair[] => {
  const [colArg, listArg] = params;
  const col = asKnownColumn(colArg, knownColumns);
  if (col === null) {
    return [];
  }
  const listShape = astShape(listArg);
  if (listShape.kind !== 'vector') {
    return [];
  }
  return listShape.elements.flatMap((el) => {
    const lit = asStringLiteral(el);
    return lit === null ? [] : [{ column: col, literal: lit }];
  });
};

/**
 * Walk a filter predicate and extract every `(= <colRef> "lit")` or
 * `(in <colRef> ["lit" …])` pair found at any nesting depth. Other
 * comparison ops (`<`, `>`, `like`, …) are skipped — fuzzy match only
 * applies to equality/membership against string literals, which is
 * where filter typos surface. Per-op pair lifting is delegated to
 * {@link equalityPair} and {@link inMembershipPairs} so this walker
 * stays a flat dispatch over node shape.
 */
const equalityLiteralsInPredicate = (
  node: unknown,
  knownColumns: ReadonlySet<string>,
): readonly FilterLiteralPair[] => {
  const shape = astShape(node);
  if (shape.kind === 'vector') {
    return shape.elements.flatMap((el) => equalityLiteralsInPredicate(el, knownColumns));
  }
  if (shape.kind !== 'opCall') {
    return [];
  }
  const { call } = shape;
  const here = pairsForCall(call, knownColumns);
  const nested = (call.params ?? []).flatMap((p) => equalityLiteralsInPredicate(p, knownColumns));
  return [...here, ...nested];
};

/**
 * Dispatch on `(= …)` / `(in …)` to lift the per-op (column, literal)
 * pairs. Flat switch over `call.name` — keeps {@link equalityLiteralsInPredicate}
 * a clean walk + nested-recursion without nested ternaries.
 */
const pairsForCall = (
  call: { readonly name: string; readonly params?: readonly unknown[] },
  knownColumns: ReadonlySet<string>,
): readonly FilterLiteralPair[] => {
  if (call.name === '=' && call.params?.length === 2) {
    return equalityPair(call.params, knownColumns);
  }
  if (call.name === 'in' && call.params?.length === 2) {
    return inMembershipPairs(call.params, knownColumns);
  }
  return [];
};

/**
 * Predicate-level SSOT for filter-literal extraction. Walks a bare
 * predicate AST (the inside of a `(filter …)` form, or a CDF view
 * source's pushed-down filter AST) and returns every `(= col "lit")` /
 * `(in col ["lit" …])` pair found at any nesting depth. Reused by
 * {@link extractFilterLiterals} for whole-AST scans and by
 * `summarizeWithRecovery` for upstream filter AST nodes that live outside
 * the chained expression.
 */
export const extractFilterLiteralsFromPredicate = (
  predicate: unknown,
  knownColumns: ReadonlySet<string>,
): readonly FilterLiteralPair[] => equalityLiteralsInPredicate(predicate, knownColumns);

/**
 * Extract every `(= <column> "literal")` / `(in <column> ["lit" …])`
 * pair from filter predicates anywhere in the AST. Reuses the shared
 * {@link walkFilterPredicates} traversal so the predicate-finding
 * never drifts from `extractFilterColumns`'s.
 */
export const extractFilterLiterals = (ast: AstInput, knownColumns: ReadonlySet<string>): readonly FilterLiteralPair[] =>
  walkFilterPredicates(ast, (pred) => extractFilterLiteralsFromPredicate(pred, knownColumns));

/**
 * Enrich a recovery `distinct` entry with `closeMatches` — fuse.js-
 * ranked "did you mean?" suggestions for each typed filter literal
 * that came close to but didn't match an actual source value. Returns
 * the entry unchanged when nothing close was typed (the agent's filter
 * literal was already correct or wasn't a string equality). Range
 * entries pass through untouched — fuzzy match is a categorical-only
 * concept.
 *
 * Two suppression guards keep the hint from misfiring:
 *
 *   - **Case-insensitive membership.** The engine's `=` op for strings
 *     is case-insensitive (see `dateAwareEquals`); a literal that the
 *     filter would have matched modulo case is NOT a typo, so we skip
 *     it. Without this, `(= status "active")` against a source value
 *     `"Active"` would spuriously suggest `'Active'` even though the
 *     filter already matches it.
 *   - **Truncated-entry abstention.** `entry.values` is the top-`MAX_
 *     ENUM_DISTINCT` (20) by frequency from the source; when
 *     `distinctCount > values.length`, a typed literal not in the
 *     displayed list might actually be a real rank-21+ source value
 *     rather than a typo, and we have no way to tell from the
 *     truncated view. Surfacing a "did you mean X?" drawn from the
 *     unrelated top-20 is misleading, so we abstain.
 */
const enrichWithCloseMatches = (entry: RecoveryEntry, filterLiterals: readonly FilterLiteralPair[]): RecoveryEntry => {
  if (entry.kind !== 'distinct' || entry.values.length === 0) {
    return entry;
  }
  // Truncated view: real rank-21+ source values are indistinguishable
  // from typos against the displayed top-20, so abstain.
  if (entry.distinctCount > entry.values.length) {
    return entry;
  }
  const typed = filterLiterals.filter((p) => p.column === entry.column).map((p) => p.literal);
  if (typed.length === 0) {
    return entry;
  }
  const valuesLower = entry.values.map((v) => v.toLowerCase());
  const closeMatches = typed.flatMap((t) => {
    // Engine `=` is case-insensitive on strings — a case-mismatched
    // literal is already matched by the filter and isn't a typo.
    if (valuesLower.includes(t.toLowerCase())) {
      return [];
    }
    const [suggestion] = closestMatches(t, entry.values, { k: 1 });
    return suggestion === undefined ? [] : [{ typed: t, suggestion }];
  });
  return closeMatches.length === 0 ? entry : { ...entry, closeMatches };
};

/**
 * Classify a `ColumnType` for recovery: `distinct` for string-like,
 * `range` for numeric/date. Returns `null` for types we cannot
 * meaningfully aggregate (struct, array, timeseries, nested-dataset,
 * null, unknown) — those columns are silently skipped from recovery.
 * The "is this a primitive leaf we can render?" guard is shared with
 * `summarizeDataset` via {@link isPrimitiveLeafKind}.
 */
export const classifyForRecovery = (type: ColumnType): 'distinct' | 'range-numeric' | 'range-date' | null => {
  const inner = unwrapNullable(type);
  if (!isPrimitiveLeafKind(inner.kind)) {
    return null;
  }
  switch (inner.kind) {
    case 'string':
    case 'boolean':
      return 'distinct';
    case 'number':
    case 'integer':
      return 'range-numeric';
    case 'timestamp':
    case 'date':
      return 'range-date';
    default:
      return null;
  }
};

/**
 * Extract one column's recovery entry from a materialised source
 * dataset, reusing the same {@link computeAutoStats} the column-profile
 * pass already runs and the shared {@link computeColumnFrequencies}
 * primitive — no per-column s-expression synthesis, no duplicate
 * min/max computation. Returns `null` for columns absent from the
 * source schema, columns of an unsupported type, or empty sources
 * with nothing to report.
 */
const recoveryEntryFromSource = (
  column: string,
  sourceMat: MaterializedDataset,
  sourceStats: ReturnType<typeof computeAutoStats>,
): RecoveryEntry | null => {
  const type = sourceMat.schema.get(column);
  if (!type) {
    return null;
  }
  const classification = classifyForRecovery(type);
  if (classification === null) {
    return null;
  }
  const stats = sourceStats[column];

  if (classification === 'distinct') {
    const cells = sourceMat.columns[column] ?? [];
    const frequencies = computeColumnFrequencies(cells);
    if (frequencies.length === 0) {
      return null;
    }
    return {
      column,
      kind: 'distinct',
      values: frequencies.slice(0, MAX_ENUM_DISTINCT).map((f) => f.value),
      distinctCount: frequencies.length,
    };
  }
  if (classification === 'range-numeric' && stats?.type === 'numeric') {
    if (stats.min === null && stats.max === null) {
      return null;
    }
    return {
      column,
      kind: 'range',
      min: stats.min === null ? null : String(stats.min),
      max: stats.max === null ? null : String(stats.max),
    };
  }
  if (classification === 'range-date' && stats?.type === 'date') {
    if (stats.min === null && stats.max === null) {
      return null;
    }
    return {
      column,
      kind: 'range',
      min: stats.min === null ? null : formatDisplayInstant(stats.min),
      max: stats.max === null ? null : formatDisplayInstant(stats.max),
    };
  }
  return null;
};

/**
 * Extend a {@link DatasetSummary} with `recoveryContext` and a note
 * when the query returned no rows OR any column came back fully null.
 * Single source fetch — `fetchSource` returns the unfiltered upstream
 * once and all target columns are derived from one
 * {@link computeAutoStats} pass over its columns.
 *
 * Filter columns and literals are extracted from the chained `expression`
 * AST — `(filter …)` predicates reachable from the top-level call,
 * including a `query` source op's trailing `(filter …)` constraint form —
 * resolved against the SOURCE schema (so refs to columns the chain later
 * renamed/selected-out aren't silently dropped).
 *
 * No-ops on healthy results, when no target columns resolve, when the
 * fetcher returns `null`, or when no entries can be produced — the
 * returned summary in those cases is reference-equal to the input.
 */
export const extendSummaryWithRecovery = async (params: {
  summary: DatasetSummary;
  expression: string;
  fetchSource: RecoverySourceFetcher;
}): Promise<DatasetSummary> => {
  const { summary, expression, fetchSource } = params;
  const allNullColumns = summary.columns
    .filter((c) => summary.rowCount > 0 && c.nullCount === summary.rowCount)
    .map((c) => c.name);
  if (summary.rowCount > 0 && allNullColumns.length === 0) {
    return summary;
  }

  // Single parse — used for both column and literal extraction below.
  let ast: AstInput | null = null;
  try {
    ast = expressionToAst(expression);
  } catch (err) {
    console.error('[recovery] failed to parse expression for filter extraction:', err);
  }

  // Fetch the source up front so its schema is available for
  // knownColumns. The source schema carries PRE-transform column names
  // — needed when the chain renamed/selected a column whose original
  // name appears in the chained filter.
  let sourceMat: MaterializedDataset | null = null;
  try {
    sourceMat = await fetchSource();
  } catch (err) {
    console.error('[recovery] failed to fetch recovery source:', err);
    return summary;
  }
  if (sourceMat === null) {
    return summary;
  }
  // Union of summary (post-transform) and source (pre-transform)
  // column names so refs from either layer of the chain resolve.
  const knownColumns = new Set([...summary.columns.map((c) => c.name), ...sourceMat.schema.keys()]);
  const filterCols = ast === null ? [] : extractFilterColumns(ast, knownColumns);
  const filterLiterals = ast === null ? [] : extractFilterLiterals(ast, knownColumns);
  const targets = Array.from(new Set([...filterCols, ...allNullColumns]));
  if (targets.length === 0) {
    return summary;
  }

  const sourceStats = computeAutoStats(sourceMat.columns);
  const recoveryContext = targets.flatMap((col) => {
    const entry = recoveryEntryFromSource(col, sourceMat, sourceStats);
    return entry ? [enrichWithCloseMatches(entry, filterLiterals)] : [];
  });

  if (recoveryContext.length === 0) {
    return summary;
  }
  // Only the facts are attached — the recovery-attached line (and its
  // concurrent reason clauses) derives from them inside `<DatasetPreview>`.
  return { ...summary, recoveryContext };
};
